import { ENDPOINTS } from "@constants";
import AbstractService from "./AbstractService";

export default class InvoiceService extends AbstractService {
	static endpoint = ENDPOINTS.invoices;

	static getCounts(queryParams) {
		return this.get({
			endpoint: `${this.endpoint}/${ENDPOINTS.counts}`,
			queryParams,
		});
	}

	static getInvoiceList({ getCancelToken, ...queryParams }) {
		return this.get({
			endpoint: `${this.endpoint}`,
			queryParams,
			getCancelToken,
		});
	}

	static getInvoiceById(id) {
		return this.get({
			endpoint: `${this.endpoint}/${id}`,
			cache: { ignoreCache: true },
		});
	}

	static getInvoiceSummary({ queryParams, data }) {
		return this.post({
			endpoint: `${this.endpoint}/transactions/`,
			queryParams,
			data,
		});
	}

	static getReceiptDetails(transactionId) {
		return this.get({
			endpoint: `${this.endpoint}/transaction/receipt`,
			queryParams: { transactionId },
		});
	}

	static getDisputeReasons() {
		return this.get({
			endpoint: `${this.endpoint}/disputeReasons/`,
		});
	}

	static submitDisputeForm(Id, Note, DisputeReasonId) {
		return this.post({
			endpoint: `${this.endpoint}/${Id}/dispute/`,
			data: {
				Note,
				DisputeReasonId,
			},
		});
	}

	static getInstantPayReasonsInvoice(id, queryParams) {
		return this.get({
			endpoint: `${this.endpoint}/${id}/validateinstantpay`,
			queryParams,
		});
	}

	static downloadReceiptPDF(id) {
		return this.request({
			endpoint: `${this.endpoint}/transaction/receipt/${id}/pdf`,
			responseType: "blob",
		});
	}

	static downloadInvoicePDF(id) {
		return this.request({
			endpoint: `${this.endpoint}/${id}/downloadpdf`,
			responseType: "blob",
		});
	}

	static updateInvoiceItems({ id, data }) {
		return this.put({
			endpoint: `${this.endpoint}/items/${id}`,
			data,
		});
	}

	static archiveInvoice(id) {
		return this.put({
			endpoint: `NonRKPayInvoices/${id}/archive`,
		});
	}

	static unarchiveInvoice(id) {
		return this.put({
			endpoint: `NonRKPayInvoices/${id}/unarchive`,
		});
	}

	static sendInvoiceToClient(queryParams, data) {
		return this.post({
			endpoint: `${this.endpoint}/transactions/client`,
			queryParams,
			data,
		});
	}

	static resendInvoiceToClient(id) {
		return this.post({
			endpoint: `${this.endpoint}/${id}/resendclient`,
		});
	}

	static recalculateInvoiceById(id) {
		return this.post({
			endpoint: `${this.endpoint}/${id}/recalculate`,
		});
	}

	static remindOfficerSubmit(id) {
		return this.post({
			endpoint: `${this.endpoint}/${id}/remind`,
		});
	}

	static changeInvoiceUserStatus({ idArray, status }) {
		return this.put({
			endpoint: `v2/${this.endpoint}/status`,
			data: {
				InvoiceIds: idArray,
				InvoiceStatus: status,
			},
		});
	}

	static updateAgencyNotes(id, data) {
		// Added headers since the endpoint accepts only a standalone string as a param
		return this.put({
			endpoint: `${this.endpoint}/${id}/agencynotes`,
			headers: { "Content-Type": "application/json" },
			data,
		});
	}

	static getNonRKPayInvoice(id) {
		return this.get({
			endpoint: `NonRKPayInvoices/${id}`,
		});
	}

	static getNonRKPayLineItems(workedJobs, data) {
		return this.post({
			endpoint: "NonRKPayInvoices/items",
			queryParams: { workedJobs },
			data,
		});
	}

	static createNonRKPayInvoice(data) {
		return this.post({
			endpoint: "NonRKPayInvoices",
			data,
		});
	}

	static updateNonRKPayInvoice(data) {
		return this.put({
			endpoint: "NonRKPayInvoices",
			data,
		});
	}

	static getNonRKPayInvoiceStatusHistory(id) {
		return this.get({
			endpoint: `NonRKPayInvoices/${id}/statushistory`,
		});
	}

	static sendManualInvoiceEmail(id, data) {
		return this.post({
			endpoint: `NonRKPayInvoices/${id}/pdf/send`,
			data,
		});
	}

	static downloadManualInvoicePDF(id) {
		return this.request({
			endpoint: `NonRKPayInvoices/${id}/pdf`,
			responseType: "blob",
		});
	}

	static updateInvoiceDisclaimer(id, Disclaimer) {
		return this.put({
			endpoint: `${this.endpoint}/${id}/disclaimer`,
			data: { Disclaimer },
		});
	}

	static voidInvoice(id) {
		return this.put({
			endpoint: `${this.endpoint}/void/`,
			data: id,
		});
	}

	static deleteManualInvoice(id) {
		return this.del({ endpoint: `NonRKPayInvoices/${id}` });
	}

	static finalizeManualInvoice(id) {
		return this.post({ endpoint: `NonRKPayInvoices/${id}/finalize` });
	}

	static unfinalizeManualInvoice(id, data) {
		return this.put({
			endpoint: `NonRKPayInvoices/${id}/unfinalize`,
			data,
		});
	}

	static refundBalance(id) {
		return this.post({ endpoint: `NonRKPayInvoices/${id}/refund` });
	}

	static refundRKInsurance(id) {
		return this.del({ endpoint: `${this.endpoint}/${id}/insurance` });
	}

	static getManualInvoiceFeeTypes() {
		return this.get({ endpoint: "NonRKPayInvoices/feetypes" });
	}

	static getManualInvoiceCalcTypes() {
		return this.get({ endpoint: "NonRKPayInvoices/calculationtypes" });
	}

	static voidEstimate(id) {
		return this.put({ endpoint: `NonRKPayInvoices/${id}/void` });
	}

	static getAllAttachments(id) {
		return this.get({ endpoint: `NonRKPayInvoices/${id}/attachments` });
	}

	static getTimeCardNotesForNonRKPayInvoice(id) {
		return this.get({ endpoint: `NonRKPayInvoices/${id}/timecardnotes` });
	}

	static downloadAllAttachments(id, data) {
		return this.post({
			endpoint: `NonRKPayInvoices/${id}/attachments/bulk`,
			data,
		});
	}

	static getNonRKPayInvoiceDraftTotals(data) {
		return this.post({
			endpoint: "NonRKPayInvoices/draft",
			data,
		});
	}

	static getInternalNotesForNonRKPayInvoice(id) {
		return this.get({ endpoint: `NonRKPayInvoices/${id}/internalnotes` });
	}

	static putInternalNotesForNonRKPayInvoice(id, internalNotes) {
		return this.put({
			endpoint: `NonRKPayInvoices/${id}/internalnotes`,
			data: internalNotes,
			headers: { "Content-Type": "application/json" },
		});
	}

	static getNonRkPayInvoicesSummary(invoiceIds) {
		return this.get({
			endpoint: "NonRKPayInvoices/summary",
			queryParams: { invoiceIds },
		});
	}

	static approveNonRKPayInvoice(data) {
		return this.put({
			endpoint: `NonRKPayInvoices/approve`,
			data,
		});
	}

	static updateNonRKPayInvoiceStatus(id, status) {
		return this.put({
			endpoint: `NonRKPayInvoices/${id}/status`,
			queryParams: { newStatusId: status },
		});
	}

	static recalculateNonRKPayInvoice(id) {
		return this.put({
			endpoint: `NonRKPayInvoices/${id}/recalculate`,
		});
	}
}
